import { styled } from '@linaria/react';
import {
  component,
  fonts,
  primitives,
  radius,
  semantic,
  size,
  space,
  textStyle,
} from '../../theme/tokens';

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space['space-100']};
`;

const stateOverlay = (overlay: string) => `inset 0 0 0 ${primitives.size['size-full']} ${overlay}`;

export const StyledButton = styled.button<{
  $fullWidth: boolean;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  font-family: ${textStyle.body.lg.strong.fontFamily};
  font-weight: ${textStyle.body.lg.strong.fontWeight};
  letter-spacing: ${textStyle.body.lg.strong.letterSpacing};
  line-height: ${textStyle.body.lg.strong.lineHeight};
  border-radius: ${radius('button', 'radius-full')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  height: ${primitives.size['size-1200']};
  padding: ${space['space-300']} ${space['space-500']};
  font-size: ${textStyle.body.lg.strong.fontSize};

  &[data-size='small'] {
    height: ${primitives.size['size-1000']};
    padding: ${space['space-100']} ${space['space-400']};
    font-size: ${textStyle.body.md.strong.fontSize};
  }

  &[data-size='large'] {
    height: ${primitives.size['size-1400']};
    padding: ${space['space-400']} ${space['space-600']};
    font-size: ${textStyle.body.lg.strong.fontSize};
  }

  background-color: ${component.button.primary.enabled.bg};
  color: ${component.button.primary.enabled.text};
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  /* The state overlay is a shadow, not a gradient, so the transition can fade it. */
  &[data-variant='primary'] {
    --_button-icon: ${component.button.primary.enabled.icon};

    /* Icons inherit colour from the label; the Figma styles tint them separately. */
    & :where(svg) {
      color: var(--_button-icon);
    }
    &:disabled {
      background-color: ${component.button.primary.disabled.bg};
      color: ${component.button.primary.disabled.text};
      --_button-icon: ${component.button.primary.disabled.icon};
      opacity: 1;
    }

    &:focus-visible {
      box-shadow: ${stateOverlay(component.button.primary.focussed.bg)};
      color: ${component.button.primary.focussed.text};
      --_button-icon: ${component.button.primary.focussed.icon};
    }

    &:hover:not(:disabled) {
      box-shadow: ${stateOverlay(component.button.primary.hover.bg)};
      color: ${component.button.primary.hover.text};
      --_button-icon: ${component.button.primary.hover.icon};
    }

    &:active:not(:disabled) {
      box-shadow: ${stateOverlay(component.button.primary.pressed.bg)};
      color: ${component.button.primary.pressed.text};
      --_button-icon: ${component.button.primary.pressed.icon};
    }
  }

  &:not([data-variant='primary']):hover:not(:disabled) {
    opacity: 0.85;
  }

  &[data-variant='secondary'] {
    background-color: ${component.button.secondary.enabled.bg};
    color: ${component.button.secondary.enabled.text};
    --_button-icon: ${component.button.secondary.enabled.icon};

    & :where(svg) {
      color: var(--_button-icon);
    }

    &:focus-visible {
      background-color: ${component.button.secondary.focussed.bg};
      color: ${component.button.secondary.focussed.text};
      --_button-icon: ${component.button.secondary.focussed.icon};
    }

    &:disabled {
      background-color: ${component.button.secondary.disabled.bg};
      color: ${component.button.secondary.disabled.text};
      --_button-icon: ${component.button.secondary.disabled.icon};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.secondary.hover.bg};
      color: ${component.button.secondary.hover.text};
      --_button-icon: ${component.button.secondary.hover.icon};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${component.button.secondary.pressed.bg};
      color: ${component.button.secondary.pressed.text};
      --_button-icon: ${component.button.secondary.pressed.icon};
    }
  }

  &[data-variant='neutral'] {
    background-color: ${component.button.neutral.enabled.bg};
    color: ${component.button.neutral.enabled.text};
    --_button-icon: ${component.button.neutral.enabled.icon};

    & :where(svg) {
      color: var(--_button-icon);
    }

    &:focus-visible {
      background-color: ${component.button.neutral.focussed.bg};
      color: ${component.button.neutral.focussed.text};
      --_button-icon: ${component.button.neutral.focussed.icon};
    }

    &:disabled {
      background-color: ${component.button.neutral.disabled.bg};
      color: ${component.button.neutral.disabled.text};
      --_button-icon: ${component.button.neutral.disabled.icon};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.neutral.hover.bg};
      color: ${component.button.neutral.hover.text};
      --_button-icon: ${component.button.neutral.hover.icon};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${component.button.neutral.pressed.bg};
      color: ${component.button.neutral.pressed.text};
      --_button-icon: ${component.button.neutral.pressed.icon};
    }
  }

  &[data-variant='outline'] {
    background-color: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};

    &:focus-visible {
      background-color: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background-color: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='toggle'] {
    background-color: transparent;
    color: ${semantic.text.primary};
    border: ${size.stroke['stroke-default']} solid ${semantic.border.default};
    border-radius: ${radius('button', 'radius-sm')};
    font-weight: ${fonts.weight.medium};

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceSubtle};
      border-color: ${semantic.border.default};
      color: ${semantic.text.primary};
    }

    &[data-selected] {
      background-color: ${semantic.bg.brand};
      border-color: ${semantic.border.default};
      color: ${semantic.text.onBrand};

      &:hover:not(:disabled) {
        background-color: ${semantic.bg.brand};
        border-color: ${semantic.border.default};
        color: ${semantic.text.onBrand};
        opacity: 0.85;
      }
    }
  }

  &[data-variant='icon'] {
    height: ${space['space-1000']};
    padding: ${space['space-200']} ${space['space-400']};
    border-radius: ${radius('button', 'radius-full')};
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      background: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceDefault};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='danger'] {
    background-color: ${component.button.danger.enabled.bg};
    color: ${component.button.danger.enabled.text};
    --_button-icon: ${component.button.danger.enabled.icon};

    & :where(svg) {
      color: var(--_button-icon);
    }

    &:focus-visible {
      background-color: ${component.button.danger.focussed.bg};
      color: ${component.button.danger.focussed.text};
      --_button-icon: ${component.button.danger.focussed.icon};
      outline-color: ${semantic.bg.urgencyStrong};
    }

    &:disabled {
      background-color: ${component.button.danger.disabled.bg};
      color: ${component.button.danger.disabled.text};
      --_button-icon: ${component.button.danger.disabled.icon};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.danger.hover.bg};
      color: ${component.button.danger.hover.text};
      --_button-icon: ${component.button.danger.hover.icon};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${component.button.danger.pressed.bg};
      color: ${component.button.danger.pressed.text};
      --_button-icon: ${component.button.danger.pressed.icon};
    }
  }

  &[data-variant='icon-ghost'] {
    height: ${space['space-1000']};
    padding: ${space['space-100']} ${space['space-400']};
    border-radius: ${radius('button', 'radius-full')};
    background: transparent;
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      outline-offset: -2px;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceDefault};
      opacity: 1;
    }
  }

  &[data-variant='icon-overlay'] {
    height: ${space['space-1000']};
    padding: ${space['space-100']} ${space['space-400']};
    border-radius: ${radius('button', 'radius-full')};
    background: ${semantic.bg.overlayBlur};
    backdrop-filter: blur(${size.blur['blur-lg']});
    color: ${semantic.text.inverse};
    border: none;

    &:focus-visible {
      background: ${semantic.bg.overlayBlurStrong};
      outline-color: ${semantic.text.inverse};
      outline-offset: -2px;
    }

    &:disabled {
      color: ${semantic.icon.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.overlayBlurStrong};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background: ${semantic.bg.overlayBlurStrong};
    }
  }
`;
